import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import {
  LocationCity as MunicipalityIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { translateCostClass } from '../utils/translationUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const COST_CLASSES = ['Ordinary', 'Increased', 'Increased Kitten', 'Increased Sickroom', 'Other'] as const;
type CostClass = typeof COST_CLASSES[number];

const DEFAULT_RATES: Record<CostClass, number> = {
  'Ordinary':            2.12,
  'Increased':           3.12,
  'Increased Kitten':    4.12,
  'Increased Sickroom':  5.12,
  'Other':               6.12,
};

interface Municipality {
  municipality: string;
}

// rates keyed by municipality → cost_class → daily_rate
type RatesMap = Record<string, Record<string, number>>;

const Municipalities: React.FC = () => {
  const { t } = useTranslation();
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [rates, setRates] = useState<RatesMap>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newRates, setNewRates] = useState<Record<CostClass, string>>({ ...Object.fromEntries(COST_CLASSES.map(c => [c, String(DEFAULT_RATES[c])])) } as Record<CostClass, string>);

  // Edit rates dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editMunicipality, setEditMunicipality] = useState('');
  const [editRates, setEditRates] = useState<Record<CostClass, string>>({} as Record<CostClass, string>);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteName, setDeleteName] = useState('');

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [mRes, rRes] = await Promise.all([
        api.get(`${API_URL}/api/tables/public/municipalities/data?limit=1000`),
        api.get(`${API_URL}/api/municipality-rates`),
      ]);

      const muns: Municipality[] = mRes.data.rows
        .map((row: any[]) => ({ municipality: row[0] }))
        .sort((a: Municipality, b: Municipality) => a.municipality.localeCompare(b.municipality));
      setMunicipalities(muns);

      const ratesMap: RatesMap = {};
      for (const row of rRes.data) {
        if (!ratesMap[row.municipality]) ratesMap[row.municipality] = {};
        ratesMap[row.municipality][row.cost_class] = parseFloat(row.daily_rate);
      }
      setRates(ratesMap);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  // ---- Add municipality ----
  const openAdd = () => {
    setNewName('');
    setNewRates({ ...Object.fromEntries(COST_CLASSES.map(c => [c, String(DEFAULT_RATES[c])])) } as Record<CostClass, string>);
    setAddOpen(true);
  };

  const handleAdd = async () => {
    if (!newName.trim()) {
      setMessage({ type: 'error', text: t('municipalities.fillAllFields') });
      return;
    }
    for (const cc of COST_CLASSES) {
      const v = parseFloat(newRates[cc]);
      if (isNaN(v) || v < 0) {
        setMessage({ type: 'error', text: t('municipalities.validPositiveNumber') });
        return;
      }
    }
    try {
      await api.post(`${API_URL}/api/records`, {
        table: 'municipalities',
        schema: 'public',
        data: { municipality: newName.trim() },
      });
      for (const cc of COST_CLASSES) {
        await api.put(`${API_URL}/api/municipality-rates/${encodeURIComponent(newName.trim())}/${encodeURIComponent(cc)}`, {
          daily_rate: parseFloat(newRates[cc]),
        });
      }
      setAddOpen(false);
      setMessage({ type: 'success', text: t('municipalities.addedSuccess') });
      loadAll();
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || err.message });
    }
  };

  // ---- Edit rates ----
  const openEdit = (mun: string) => {
    setEditMunicipality(mun);
    const current = rates[mun] ?? {};
    setEditRates({ ...Object.fromEntries(COST_CLASSES.map(c => [c, String(current[c] ?? DEFAULT_RATES[c])])) } as Record<CostClass, string>);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    for (const cc of COST_CLASSES) {
      const v = parseFloat(editRates[cc]);
      if (isNaN(v) || v < 0) {
        setMessage({ type: 'error', text: t('municipalities.validPositiveNumber') });
        return;
      }
    }
    try {
      await Promise.all(COST_CLASSES.map(cc =>
        api.put(`${API_URL}/api/municipality-rates/${encodeURIComponent(editMunicipality)}/${encodeURIComponent(cc)}`, {
          daily_rate: parseFloat(editRates[cc]),
        })
      ));
      setEditOpen(false);
      setMessage({ type: 'success', text: t('municipalities.updatedSuccess') });
      loadAll();
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || err.message });
    }
  };

  // ---- Delete ----
  const openDelete = (mun: string) => {
    setDeleteName(mun);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`${API_URL}/api/records`, {
        data: { table: 'municipalities', schema: 'public', record_id: deleteName, id_column: 'municipality' },
      });
      setDeleteOpen(false);
      setMessage({ type: 'success', text: t('municipalities.deletedSuccess') });
      loadAll();
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || err.message });
    }
  };

  const rateField = (
    label: string,
    value: string,
    onChange: (v: string) => void
  ) => (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      type="number"
      size="small"
      fullWidth
      inputProps={{ min: 0, step: 0.01 }}
      InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>€</span> }}
      sx={{ mb: 1.5 }}
    />
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, background: 'white', boxShadow: '0 4px 16px rgba(102,126,234,0.12)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5, boxShadow: '0 3px 8px rgba(102,126,234,0.35)' }}>
              <MunicipalityIcon sx={{ fontSize: 24, color: 'white' }} />
            </Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#667eea' }}>
              {t('municipalities.title')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', px: 3, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '1rem', fontWeight: 600, boxShadow: '0 4px 12px rgba(102,126,234,0.4)', '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' } }}
          >
            {t('municipalities.addMunicipality')}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('municipalities.description')}
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.light' }}>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>{t('cat.municipality')}</TableCell>
                {COST_CLASSES.map(cc => (
                  <TableCell key={cc} align="right" sx={{ fontWeight: 600, color: 'white', whiteSpace: 'nowrap' }}>
                    {translateCostClass(t, cc)}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 600, color: 'white' }}>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center">{t('common.loading')}</TableCell></TableRow>
              ) : municipalities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">{t('municipalities.noMunicipalitiesFound')}</Typography>
                  </TableCell>
                </TableRow>
              ) : municipalities.map((mun) => (
                <TableRow key={mun.municipality} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{mun.municipality}</TableCell>
                  {COST_CLASSES.map(cc => (
                    <TableCell key={cc} align="right">
                      €{(rates[mun.municipality]?.[cc] ?? DEFAULT_RATES[cc]).toFixed(2)}
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(mun.municipality)} sx={{ color: '#667eea', mr: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => openDelete(mun.municipality)} sx={{ color: 'error.main' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>{t('municipalities.addNewMunicipality')}</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField
            label={t('municipalities.municipalityName')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            size="small"
            fullWidth
            required
            sx={{ mb: 2, mt: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('municipalities.dailyRatesPerClass')}
          </Typography>
          {COST_CLASSES.map(cc => rateField(
            translateCostClass(t, cc),
            newRates[cc],
            (v) => setNewRates({ ...newRates, [cc]: v })
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button onClick={handleAdd} variant="contained" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 2 }}>
            {t('municipalities.addMunicipality')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Rates Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {t('municipalities.editRates')} — {editMunicipality}
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('municipalities.dailyRatesPerClass')}
          </Typography>
          {COST_CLASSES.map(cc => rateField(
            translateCostClass(t, cc),
            editRates[cc] ?? '',
            (v) => setEditRates({ ...editRates, [cc]: v })
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button onClick={handleEditSave} variant="contained" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 2 }}>
            {t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>{t('municipalities.deleteMunicipality')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('users.confirmDeleteUser')} <strong>{deleteName}</strong>?
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {t('municipalities.confirmDeleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button onClick={handleDelete} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 2 }}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Municipalities;
